// Set bonuses that are not plain stats (5-piece bonuses and Medic 3). Each one is a
// named hook called from combat, encounters or the expedition tick, so the rule
// lives in one place and the call sites read like the design.

import { ZONES } from '../content/zones';
import type { Rng } from '../rng';
import type { CombatStats, SetHookId, ZoneId } from '../types';
import { perkSum } from './perks';

export const SET_HOOK_TUNING = {
  /** Scrapper 5 */
  reflectChance: 0.15,
  reflectShare: 0.2,
  /** Medic 5 */
  retreatHeal: 0.15,
  /** Nightstalker 5 */
  followExtraTicks: 2,
  /** Military 5 */
  militaryZoneBoost: 0.1,
} as const;

export function hasSetHook(stats: Pick<CombatStats, 'setHooks'> | undefined, hook: SetHookId): boolean {
  return !!stats?.setHooks?.includes(hook);
}

/**
 * Drifter 5: the first escape attempt of each expedition always works. The server
 * sets `guaranteedEscape` while the expedition has not used it yet.
 */
export function onEscapeAttempt(runner: { guaranteedEscape?: boolean }, chance: number): number {
  return runner.guaranteedEscape ? 1 : chance;
}

/**
 * Scrapper 5: when a melee hit lands, 15% chance to send 20% of its damage back to
 * the attacker. Returns the reflected damage (0 when nothing happens).
 */
export function onMeleeHitTaken(defender: CombatStats, attacker: CombatStats, dmg: number, rng: Rng): number {
  if (attacker.ranged || dmg <= 0 || !hasSetHook(defender, 'scrapper_reflect')) return 0;
  if (!rng.chance(SET_HOOK_TUNING.reflectChance, 'reflect')) return 0;
  return Math.max(1, Math.round(dmg * SET_HOOK_TUNING.reflectShare));
}

/** Medic 5: HP restored when this survivor gets away from a fight. */
export function onRetreatFromFight(stats: CombatStats, hpMax: number): number {
  return hasSetHook(stats, 'medic_retreat_heal') ? Math.round(hpMax * SET_HOOK_TUNING.retreatHeal) : 0;
}

/**
 * HP after a fight: Field Medic and Second Wind heal after a win, Medic 3 after
 * every fight, Medic 5 when the survivor got away. A survivor at 0 HP is not healed.
 */
export function onFightEnd(stats: CombatStats, hp: number, hpMax: number, result: { won: boolean; escaped?: boolean }): number {
  if (hp <= 0) return hp;
  let heal = Math.round(hpMax * ((result.won ? stats.fieldMedic : 0) + (stats.postFightHeal ?? 0)));
  if (result.escaped) heal += onRetreatFromFight(stats, hpMax);
  return Math.min(hpMax, hp + heal);
}

/** Nightstalker 5 and Patience / Silent Stride (perks): extra ticks a follow lasts before the Shadow must strike. */
export function followExtraTicks(stats: CombatStats): number {
  return (hasSetHook(stats, 'nightstalker_tracking') ? SET_HOOK_TUNING.followExtraTicks : 0) + perkSum(stats.perkFx, 'followTicks');
}

/** Nightstalker 5: the target gets no detection roll on the first tick of a follow. */
export function skipsFirstFollowDetection(stats: CombatStats): boolean {
  return hasSetHook(stats, 'nightstalker_tracking');
}

/** Military 5 applies in the Military Base and in every open-PvP zone. */
export function militaryZone(zone: ZoneId | undefined): boolean {
  if (!zone) return false;
  return zone === 'military_base' || ZONES[zone]?.pvp === 'open';
}
